'use client';

import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { FaRegCheckCircle, FaRegTimesCircle } from "react-icons/fa";
import {
    HiOutlineUser,
    HiOutlineEnvelope,
    HiOutlinePhone,
    HiOutlineCalendar,
    HiOutlineCheck,
    HiOutlineUserCircle
} from "react-icons/hi2";
import dayjs from "dayjs";
import { useToast } from "../../components/ui/ToastContext";
import { API_URL } from "../../config";

type PhysicianData = {
    physician_id: number;
    user_name_surname: string;
    email: string;
    phone: number | string;
    birth: string;
    is_active: boolean | number;
    data_acord: boolean | number;
};

interface PhysiciansDetailsFormProps {
    onSaveChanges: () => void;
}

export const PhysicianDetailsForm: React.FC<PhysiciansDetailsFormProps> = ({ onSaveChanges }) => {
    const { showToast } = useToast();
    const location = useLocation();
    const { item: initialData } = location.state || {};
    const [selectedEdit, setSelectedEdit] = useState(initialData);

    const [selectedEditRowData, setSelectedEditRowData] = useState<PhysicianData | null>(initialData || null);
    const [Nume_prenume, setNume_prenume] = useState(initialData?.user_name_surname || '');
    const [Birth, setBirth] = useState('');
    const [mail, setMail] = useState(initialData?.email || '');
    const [phone, setPhone] = useState(initialData?.phone ? String(initialData.phone) : '');
    const [isActive, setIsActive] = useState<boolean>(initialData?.is_active !== undefined ? Boolean(initialData.is_active) : true);
    const [emailError, setEmailError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showGrid, setShowGrid] = useState(true);

    const token = localStorage.getItem('jwtToken');

    useEffect(() => {
        const timer = setTimeout(() => {
            setShowGrid(false);
        }, 350);
        return () => clearTimeout(timer);
    }, []);

    const fetchPhysician = async () => {
        if (!selectedEdit?.physician_id) return;
        try {
            const response = await axios.get(`${API_URL}/api/physician`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            const found = response.data.find((item: any) => item.physician_id === selectedEdit.physician_id);
            if (found) {
                setSelectedEditRowData(found);
                setNume_prenume(found.user_name_surname || '');
                setMail(found.email || '');
                setPhone(found.phone ? String(found.phone) : '');
                if (found.birth) {
                    const formattedDate = dayjs(found.birth).add(1, 'day').format("YYYY-MM-DD");
                    setBirth(formattedDate);
                } else {
                    setBirth('');
                }
                setIsActive(Boolean(found.is_active));
            }
        } catch (error) {
            console.error("Error fetching physician data:", error);
        }
    };

    useEffect(() => {
        fetchPhysician();
    }, [selectedEdit]);

    const validateEmail = (emailStr: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(emailStr);
    };

    const handleChangem = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setMail(value);
        if (value && !validateEmail(value)) {
            setEmailError(true);
        } else {
            setEmailError(false);
        }
    };

    const handleChangep = (e: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = e.target.value.replace(/\D/g, '').slice(0, 9);
        setPhone(rawValue);
    };

    const handleSaveChanges = async () => {
        const targetId = selectedEditRowData?.physician_id || initialData?.physician_id;
        if (!targetId) return;

        if (mail && !validateEmail(mail)) {
            showToast({
                message: "Formatul adresei de email este invalid!",
                type: "error",
                icon: <FaRegTimesCircle className="text-red-600 dark:text-red-500" />,
            });
            return;
        }

        try {
            const phoneNumber = phone ? Number(phone) : null;
            // Preservam data_acord exact cum este in baza de date (fara a permite editarea manuala de aici)
            const currentDataAcord = selectedEditRowData?.data_acord !== undefined 
                ? (selectedEditRowData.data_acord ? 1 : 0) 
                : (initialData?.data_acord ? 1 : 0);

            await axios.put(
                `${API_URL}/api/changephysician/${targetId}`,
                {
                    user_name_surname: Nume_prenume,
                    phone: phoneNumber || '',
                    email: mail || '',
                    birth: Birth || null,
                    data_acord: currentDataAcord,
                    is_active: isActive ? 1 : 0
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    }
                }
            );

            showToast({
                message: "Datele medicului au fost salvate cu succes!",
                type: "success",
                icon: <FaRegCheckCircle className="text-emerald-600 dark:text-emerald-500" />,
            });
            onSaveChanges();
            fetchPhysician();
        } catch (error) {
            console.error("Error saving physician changes:", error);
            showToast({
                message: "A apărut o eroare la salvarea modificărilor.",
                type: "error",
                icon: <FaRegTimesCircle className="text-red-600 dark:text-red-500" />,
            });
        }
    };

    const handleButtonClick = async () => {
        setIsLoading(true);
        try {
            await handleSaveChanges();
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="w-full h-full">
            {showGrid ? (
                <div className="bg-white dark:bg-gray-800 border border-[#E7EAF0] dark:border-gray-700/60 rounded-2xl p-6 shadow-xs animate-pulse flex flex-col justify-between h-full min-h-[300px]">
                    <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-40"></div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-4">
                        <div className="h-11 bg-gray-100 dark:bg-gray-700/60 rounded-xl"></div>
                        <div className="h-11 bg-gray-100 dark:bg-gray-700/60 rounded-xl"></div>
                        <div className="h-11 bg-gray-100 dark:bg-gray-700/60 rounded-xl"></div>
                        <div className="h-11 bg-gray-100 dark:bg-gray-700/60 rounded-xl"></div>
                    </div>
                    <div className="h-10 w-44 bg-gray-200 dark:bg-gray-700 rounded-xl self-end"></div>
                </div>
            ) : (
                <div className="bg-white dark:bg-gray-800 border border-[#E7EAF0] dark:border-gray-700/60 rounded-2xl p-6 shadow-xs flex flex-col justify-between h-full min-h-[300px] transition-all duration-200">
                    {/* Header */}
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-[#ECE8FF] text-[#6755E8] dark:bg-indigo-950/60 dark:text-indigo-400 flex items-center justify-center shrink-0">
                                <HiOutlineUserCircle className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="text-sm font-bold text-[#1E293B] dark:text-white leading-tight">
                                    Informații medic
                                </h3>
                                <p className="text-xs text-[#64748B] dark:text-gray-400 mt-0.5 leading-tight">
                                    Actualizează datele personale și de contact ale medicului.
                                </p>
                            </div>
                        </div>

                        {/* Status Toggle Pill (Only Activ/Inactiv) */}
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => setIsActive(!isActive)}
                                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-colors cursor-pointer ${
                                    isActive
                                        ? "bg-[#E8F8EE] text-[#128842] hover:bg-[#d8f5e1] dark:bg-emerald-950/60 dark:text-emerald-300"
                                        : "bg-[#FEECEB] text-[#E03131] hover:bg-[#fad3d1] dark:bg-rose-950/60 dark:text-rose-300"
                                }`}
                                title="Apasă pentru a schimba statusul activ al medicului"
                            >
                                <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-[#18A957]' : 'bg-[#E03131]'}`}></span>
                                <span>{isActive ? "Activ" : "Inactiv"}</span>
                            </button>
                        </div>
                    </div>

                    {/* Form Fields */}
                    <form onSubmit={(e) => e.preventDefault()} className="mt-5 space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {/* Nume Prenume */}
                            <div>
                                <label className="block text-xs font-semibold text-[#1E293B] dark:text-gray-200 mb-1.5">
                                    Nume Prenume
                                </label>
                                <div className="relative">
                                    <HiOutlineUser className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                                    <input
                                        type="text"
                                        placeholder="Nume Prenume"
                                        value={Nume_prenume}
                                        onChange={(e) => setNume_prenume(e.target.value)}
                                        className="w-full h-11 pl-10 pr-4 rounded-xl border border-[#DDE2EA] hover:border-[#CBD5E1] dark:border-gray-600 bg-white dark:bg-gray-700/50 text-xs font-medium text-[#1E293B] dark:text-gray-100 placeholder:text-gray-400 focus:border-[#6755E8] focus:ring-2 focus:ring-[#6755E8]/10 focus:outline-none transition-all"
                                    />
                                </div>
                            </div>

                            {/* Zi de naștere */}
                            <div>
                                <label className="block text-xs font-semibold text-[#1E293B] dark:text-gray-200 mb-1.5">
                                    Zi de naștere
                                </label>
                                <div className="relative">
                                    <HiOutlineCalendar className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                                    <input
                                        type="date"
                                        value={Birth}
                                        onChange={(e) => setBirth(e.target.value)}
                                        className="w-full h-11 pl-10 pr-4 rounded-xl border border-[#DDE2EA] hover:border-[#CBD5E1] dark:border-gray-600 bg-white dark:bg-gray-700/50 text-xs font-medium text-[#1E293B] dark:text-gray-100 focus:border-[#6755E8] focus:ring-2 focus:ring-[#6755E8]/10 focus:outline-none transition-all"
                                    />
                                </div>
                            </div>

                            {/* Poștă electronică */}
                            <div>
                                <label className="block text-xs font-semibold text-[#1E293B] dark:text-gray-200 mb-1.5">
                                    Poștă electronică
                                </label>
                                <div className="relative">
                                    <HiOutlineEnvelope className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                                    <input
                                        type="email"
                                        placeholder="doctor@domeniu.md"
                                        value={mail}
                                        onChange={handleChangem}
                                        className={`w-full h-11 pl-10 pr-4 rounded-xl border bg-white dark:bg-gray-700/50 text-xs font-medium text-[#1E293B] dark:text-gray-100 placeholder:text-gray-400 focus:ring-2 focus:outline-none transition-all ${
                                            emailError
                                                ? "border-red-500 focus:border-red-500 focus:ring-red-500/10"
                                                : "border-[#DDE2EA] hover:border-[#CBD5E1] dark:border-gray-600 focus:border-[#6755E8] focus:ring-[#6755E8]/10"
                                        }`}
                                    />
                                </div>
                                {emailError && (
                                    <p className="text-red-500 text-[10px] font-medium mt-1 leading-none">
                                        Email invalid (ex: nume@domeniu.md).
                                    </p>
                                )}
                            </div>

                            {/* Număr de telefon */}
                            <div>
                                <label className="block text-xs font-semibold text-[#1E293B] dark:text-gray-200 mb-1.5">
                                    Număr de telefon
                                </label>
                                <div className="relative">
                                    <HiOutlinePhone className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                                    <input
                                        type="tel"
                                        placeholder="069123456"
                                        value={phone}
                                        onChange={handleChangep}
                                        className="w-full h-11 pl-10 pr-4 rounded-xl border border-[#DDE2EA] hover:border-[#CBD5E1] dark:border-gray-600 bg-white dark:bg-gray-700/50 text-xs font-medium text-[#1E293B] dark:text-gray-100 placeholder:text-gray-400 focus:border-[#6755E8] focus:ring-2 focus:ring-[#6755E8]/10 focus:outline-none transition-all"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Save Action Footer */}
                        <div className="flex justify-end pt-3">
                            <button
                                type="button"
                                onClick={handleButtonClick}
                                disabled={isLoading}
                                className="inline-flex items-center gap-2 h-10 px-5 rounded-xl bg-[#1E293B] hover:bg-[#0F172A] text-white text-xs font-semibold shadow-xs transition-all duration-150 hover:-translate-y-0.5 cursor-pointer disabled:opacity-50 disabled:pointer-events-none"
                            >
                                {isLoading ? (
                                    <svg className="animate-spin w-4 h-4 text-white" viewBox="0 0 24 24" fill="none">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                                    </svg>
                                ) : (
                                    <HiOutlineCheck className="w-4 h-4" />
                                )}
                                <span>{isLoading ? "Se salvează..." : "Salvează detaliile"}</span>
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};
